import { create } from 'zustand';

/**
 * Quick task 260907-hmv: tracks whether the CURRENT kiosk session has expired and cannot be
 * renewed — distinct from `KioskContext`'s `isAuthenticated`/`maja-kiosk-authenticated`, because
 * the manual key-entry setup path (`KioskSetup.handleSubmit`) deliberately never authenticates at
 * all. "Not authenticated" and "the session I had is gone and the device must be set up again"
 * are different facts, and only the second one may force a device that is otherwise fully set up
 * (has a `kioskKey`) back to the setup screen. Reusing `isAuthenticated` for this would either
 * force setup on a device that was only ever using the manual fallback, or fail to force it at
 * all for a device that authenticated via QR and later lost its session.
 *
 * `KIOSK_AUTH_STORAGE` below MUST stay byte-identical to the literal `KioskContext.tsx` uses for
 * `maja-kiosk-authenticated` — the two files intentionally do not import from each other so that
 * `KioskContext`'s existing shape is left untouched; each file names the other in a comment so a
 * future rename of either cannot silently desynchronise them.
 */
export const KIOSK_SESSION_EXPIRED_STORAGE = 'maja-kiosk-session-expired';

/**
 * Mirrors the exact literal `KioskContext.tsx` uses for its own `KIOSK_AUTH_STORAGE` constant.
 * Kept here (not imported from `KioskContext.tsx`) to avoid coupling a context module to a store
 * module for a single string; if you rename this, rename the matching constant there too.
 */
export const KIOSK_AUTH_STORAGE = 'maja-kiosk-authenticated';

export interface KioskSessionState {
  expired: boolean;
  markExpired: () => void;
  clearExpired: () => void;
}

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function readExpiredFromStorage(): boolean {
  const storage = getStorage();
  if (!storage) return false;
  try {
    return storage.getItem(KIOSK_SESSION_EXPIRED_STORAGE) === 'true';
  } catch {
    return false;
  }
}

const initialExpired = readExpiredFromStorage();

export const useKioskSessionStore = create<KioskSessionState>((set) => ({
  expired: initialExpired,

  markExpired: () => {
    set({ expired: true });
    const storage = getStorage();
    if (!storage) return;
    try {
      storage.setItem(KIOSK_SESSION_EXPIRED_STORAGE, 'true');
      // Clearing the auth flag is deliberate: a session that cannot be renewed is no longer an
      // authenticated one, and KioskLayout must not render the kiosk shell for it.
      storage.removeItem(KIOSK_AUTH_STORAGE);
      // maja-kiosk-key is intentionally left in place — the operator should not have to retype
      // it, only re-scan the setup QR to obtain a fresh session.
    } catch {
      /* localStorage may throw in private mode / quota; degrades to in-memory only */
    }
  },

  clearExpired: () => {
    set({ expired: false });
    const storage = getStorage();
    if (!storage) return;
    try {
      storage.removeItem(KIOSK_SESSION_EXPIRED_STORAGE);
    } catch {
      /* ignore */
    }
  },
}));
